import { useState, useMemo, useCallback, useEffect } from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { DateTimePicker } from '@/components/DateTimePicker';
import { ConfirmModal } from '@/components/ConfirmModal';

// ---- types ----

interface OffenderData {
  firstName: string;
  middleName: string;
  lastName: string;
  suffix: string;
  dob: Date | null;
  driversLicenseNum: string;
  driversLicenseState: string;
  phoneNumber: string;
  address: string;
  city: string;
  state: string;
  zip: string;
}

interface CitationOffenderProps {
  value: any;
  npsdPersonId?: number | null;
  isReadOnly?: boolean;
  isLocked?: boolean;
  onInput?: (data: any) => void;
  onChange?: (data: any) => void;
  onLinkPerson?: () => void;
  onCreatePerson?: (data: any) => void;
  onUnlinkPerson?: () => void;
  onShowLinkedPerson?: () => void;
  onClear?: () => void;
}

// ---- helpers ----

function createEmptyData(): OffenderData {
  return {
    firstName: '',
    middleName: '',
    lastName: '',
    suffix: '',
    dob: null,
    driversLicenseNum: '',
    driversLicenseState: '',
    phoneNumber: '',
    address: '',
    city: '',
    state: '',
    zip: '',
  };
}

function parseValueToData(val: any): OffenderData {
  if (!val) return createEmptyData();
  return {
    firstName: val.firstName || '',
    middleName: val.middleName || '',
    lastName: val.lastName || '',
    suffix: val.suffix || '',
    dob: val.dob ? new Date(val.dob) : null,
    driversLicenseNum: val.driversLicenseNum || '',
    driversLicenseState: val.driversLicenseState || val.driversLicenseStateValue || '',
    phoneNumber: val.phoneNumber || '',
    address: val.address || val.locAddress || '',
    city: val.city || val.locCity || '',
    state: val.state || val.locState || '',
    zip: val.zip || val.locZip || '',
  };
}

// ---- component ----

export function CitationOffender({
  value,
  npsdPersonId,
  isReadOnly = false,
  isLocked = false,
  onInput,
  onChange,
  onLinkPerson,
  onCreatePerson,
  onUnlinkPerson,
  onShowLinkedPerson,
  onClear,
}: CitationOffenderProps) {
  const { theme } = useTheme();

  // ---- state ----

  const [localData, setLocalData] = useState<OffenderData>(() => parseValueToData(value));
  const [showClearConfirm, setShowClearConfirm] = useState(false);

  // ---- computed ----

  const isLinked = useMemo(
    () => npsdPersonId !== null && npsdPersonId !== undefined,
    [npsdPersonId]
  );

  const isFieldsDisabled = useMemo(
    () => isReadOnly || isLocked || isLinked,
    [isReadOnly, isLocked, isLinked]
  );

  const canLink = useMemo(
    () => !isReadOnly && !isLocked && !isLinked,
    [isReadOnly, isLocked, isLinked]
  );

  const canCreate = useMemo(
    () => !isReadOnly && !isLocked && !isLinked && !!(localData.firstName && localData.lastName),
    [isReadOnly, isLocked, isLinked, localData.firstName, localData.lastName]
  );

  const canUnlink = useMemo(
    () => !isReadOnly && !isLocked && isLinked,
    [isReadOnly, isLocked, isLinked]
  );

  // ---- sync value prop to localData ----

  useEffect(() => {
    setLocalData(parseValueToData(value));
  }, [value]);

  // ---- emit helper ----

  const emitChange = useCallback(
    (data: OffenderData) => {
      const copy = { ...data };
      onInput?.(copy);
      onChange?.(copy);
    },
    [onInput, onChange]
  );

  // ---- field change handler ----

  const handleFieldChange = useCallback(
    (field: keyof OffenderData, fieldValue: string) => {
      setLocalData((prev) => {
        const next = { ...prev, [field]: fieldValue };
        emitChange(next);
        return next;
      });
    },
    [emitChange]
  );

  // ---- dob change ----

  const handleDobChange = useCallback(
    (val: Date | null) => {
      setLocalData((prev) => {
        const next = { ...prev, dob: val };
        emitChange(next);
        return next;
      });
    },
    [emitChange]
  );

  // ---- actions ----

  const handleLinkPerson = useCallback(() => {
    if (isReadOnly || isLocked) return;
    onLinkPerson?.();
  }, [isReadOnly, isLocked, onLinkPerson]);

  const handleCreatePerson = useCallback(() => {
    if (isReadOnly || isLocked) return;
    if (!localData.firstName || !localData.lastName) {
      if (typeof (window as any).App?.showPopupNotification === 'function') {
        (window as any).App.showPopupNotification(
          'First name and last name are required to create a person.',
          'error'
        );
      }
      return;
    }
    onCreatePerson?.({ ...localData });
  }, [isReadOnly, isLocked, localData, onCreatePerson]);

  const handleUnlinkPerson = useCallback(() => {
    if (isReadOnly || isLocked) return;
    onUnlinkPerson?.();
  }, [isReadOnly, isLocked, onUnlinkPerson]);

  const handleShowLinkedPerson = useCallback(() => {
    if (isLinked) {
      onShowLinkedPerson?.();
    }
  }, [isLinked, onShowLinkedPerson]);

  const handleOpenClearConfirm = useCallback(() => {
    if (isReadOnly || isLocked) return;
    setShowClearConfirm(true);
  }, [isReadOnly, isLocked]);

  const handleConfirmClear = useCallback(() => {
    setShowClearConfirm(false);
    setLocalData(createEmptyData());
    onClear?.();
  }, [onClear]);

  const handleCancelClear = useCallback(() => {
    setShowClearConfirm(false);
  }, []);

  // ---- render ----

  return (
    <div className={`citation-offender ${theme}`}>
      <div className="offender-grid">
        {/* Row 1: Names */}
        <div className="offender-row offender-row--names">
          <div className="offender-field">
            <label>First Name</label>
            <input
              type="text"
              className="form-input"
              value={localData.firstName}
              disabled={isFieldsDisabled}
              onChange={(e) => handleFieldChange('firstName', e.target.value)}
              placeholder="First name"
            />
          </div>
          <div className="offender-field">
            <label>Last Name</label>
            <input
              type="text"
              className="form-input"
              value={localData.lastName}
              disabled={isFieldsDisabled}
              onChange={(e) => handleFieldChange('lastName', e.target.value)}
              placeholder="Last name"
            />
          </div>
          <div className="offender-field">
            <label>Middle Name</label>
            <input
              type="text"
              className="form-input"
              value={localData.middleName}
              disabled={isFieldsDisabled}
              onChange={(e) => handleFieldChange('middleName', e.target.value)}
              placeholder="Middle name"
            />
          </div>
          <div className="offender-field offender-field--small">
            <label>Suffix</label>
            <input
              type="text"
              className="form-input"
              value={localData.suffix}
              disabled={isFieldsDisabled}
              onChange={(e) => handleFieldChange('suffix', e.target.value)}
              placeholder="Jr, Sr"
              maxLength={10}
            />
          </div>
        </div>

        {/* Row 2: DOB, DL State, DL# */}
        <div className="offender-row offender-row--id">
          <div className="offender-field">
            <label>Date of Birth</label>
            <DateTimePicker
              value={localData.dob}
              type="date"
              format="DD MMM YYYY"
              valueType="date"
              clearable={true}
              appendToBody={true}
              popupClass={theme}
              disabled={isFieldsDisabled}
              onChange={handleDobChange}
            />
          </div>
          <div className="offender-field offender-field--small">
            <label>DL State</label>
            <input
              type="text"
              className="form-input"
              value={localData.driversLicenseState}
              disabled={isFieldsDisabled}
              onChange={(e) => handleFieldChange('driversLicenseState', e.target.value)}
              placeholder="ST"
              maxLength={2}
            />
          </div>
          <div className="offender-field">
            <label>Driver's License #</label>
            <input
              type="text"
              className="form-input"
              value={localData.driversLicenseNum}
              disabled={isFieldsDisabled}
              onChange={(e) => handleFieldChange('driversLicenseNum', e.target.value)}
              placeholder="License number"
            />
          </div>
        </div>

        {/* Row 3: Phone */}
        <div className="offender-row">
          <div className="offender-field">
            <label>Phone</label>
            <input
              type="text"
              className="form-input"
              value={localData.phoneNumber}
              disabled={isFieldsDisabled}
              onChange={(e) => handleFieldChange('phoneNumber', e.target.value)}
              placeholder="Phone number"
            />
          </div>
        </div>

        {/* Row 4: Address */}
        <div className="offender-row">
          <div className="offender-field offender-field--wide">
            <label>Address</label>
            <input
              type="text"
              className="form-input"
              value={localData.address}
              disabled={isFieldsDisabled}
              onChange={(e) => handleFieldChange('address', e.target.value)}
              placeholder="Street address"
            />
          </div>
        </div>

        {/* Row 5: City, State, Zip */}
        <div className="offender-row offender-row--address-detail">
          <div className="offender-field">
            <label>City</label>
            <input
              type="text"
              className="form-input"
              value={localData.city}
              disabled={isFieldsDisabled}
              onChange={(e) => handleFieldChange('city', e.target.value)}
              placeholder="City"
            />
          </div>
          <div className="offender-field offender-field--small">
            <label>State</label>
            <input
              type="text"
              className="form-input"
              value={localData.state}
              disabled={isFieldsDisabled}
              onChange={(e) => handleFieldChange('state', e.target.value)}
              placeholder="ST"
              maxLength={2}
            />
          </div>
          <div className="offender-field offender-field--small">
            <label>Zip</label>
            <input
              type="text"
              className="form-input"
              value={localData.zip}
              disabled={isFieldsDisabled}
              onChange={(e) => handleFieldChange('zip', e.target.value)}
              placeholder="Zip"
            />
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="offender-actions">
        {isLinked ? (
          <>
            <span className="linked-badge">
              <i className="fas fa-link" /> Linked to Person Record
            </span>
            <button
              type="button"
              className="btn btn-link-action"
              onClick={handleShowLinkedPerson}
              title="View linked person"
            >
              View
            </button>
            <button
              type="button"
              className="btn btn-link-action"
              onClick={handleUnlinkPerson}
              disabled={isReadOnly || isLocked}
              title="Unlink from person record"
            >
              Unlink
            </button>
          </>
        ) : (
          <>
            <div className="offender-actions-left">
              <button
                type="button"
                className="btn btn-link-action"
                onClick={handleLinkPerson}
                disabled={isReadOnly || isLocked}
                title="Link to existing person"
              >
                <i className="fas fa-link" /> Link
              </button>
              <button
                type="button"
                className="btn btn-link-action btn-create"
                onClick={handleCreatePerson}
                disabled={!canCreate}
                title="Create new person record"
              >
                <i className="fas fa-plus" /> Create
              </button>
            </div>
            <div className="offender-actions-right">
              <button
                type="button"
                className="btn btn-link-action btn-clear"
                onClick={handleOpenClearConfirm}
                disabled={isReadOnly || isLocked}
                title="Clear all offender data"
              >
                <i className="fas fa-times" /> Clear
              </button>
            </div>
          </>
        )}
      </div>

      <ConfirmModal
        open={showClearConfirm}
        title="Clear Offender Data?"
        confirmText="Clear"
        cancelText="Cancel"
        autoFocus="cancel"
        theme={theme}
        onConfirm={handleConfirmClear}
        onCancel={handleCancelClear}
      >
        <p>This will clear all entered offender information. This action cannot be undone.</p>
      </ConfirmModal>
    </div>
  );
}

(window as any).CitationOffenderReact = CitationOffender;
